/**
 * Canonical purchase-line arithmetic (PURCHASE-LINE-ROUND-STAGE-1).
 *
 * The single source of truth for how the purchase create/edit UI validates,
 * displays, and submits a line. It reproduces the backend's
 * `utils.quantity.canonical_purchase_line` (Decimal q2 / ROUND_HALF_UP) EXACTLY
 * using native `BigInt` scaled-integer arithmetic -- never `Number`,
 * `Math.round`, or `.toFixed()` for any accounting value -- so the amount the
 * user sees is the amount the backend persists.
 *
 * Scaled-integer representation of validated (<= 2dp, non-negative) inputs:
 *   - quantity   -> hundredths            (qty * 100)
 *   - unit cost  -> cents                 (cost * 100)
 *   - discount   -> hundredths of a percent (discount * 100), range 0..10000
 *
 * Backend parity is pinned by frontend tests against the checked-in,
 * backend-authored fixture `__fixtures__/purchaseLineCanonical.json`.
 */

// --------------------------------------------------------------------------
// Raw-text validation (decimal-safe; operates on the literal typed text)
// --------------------------------------------------------------------------

/** Strict accepted grammar for a non-negative <=2dp decimal, after trimming.
 * Rejects: scientific notation, sign, thousands separators, internal
 * whitespace, multiple dots, empty, and a lone ".". Trailing zeros are
 * allowed here (significance is judged separately). */
const ACCEPTED_RE = /^(?:\d+\.?\d*|\.\d+)$/;

/** True iff `raw` is a syntactically acceptable non-negative decimal number.
 * Empty string and a lone "." are rejected. */
export function isAcceptedNumberFormat(raw: string): boolean {
  const s = (raw ?? "").trim();
  if (s === "" || s === ".") return false;
  return ACCEPTED_RE.test(s);
}

/** Number of SIGNIFICANT fractional digits (trailing zeros stripped).
 * Returns null when the format is not an accepted decimal. */
export function significantDecimals(raw: string): number | null {
  if (!isAcceptedNumberFormat(raw)) return null;
  const s = (raw ?? "").trim();
  const dot = s.indexOf(".");
  if (dot < 0) return 0;
  const frac = s.slice(dot + 1).replace(/0+$/, "");
  return frac.length;
}

/** True iff `raw` carries MORE than two significant decimals (i.e. must be
 * rejected, not silently rounded). An invalid format is also "not ok". */
export function exceeds2dp(raw: string): boolean {
  const n = significantDecimals(raw);
  return n === null || n > 2;
}

/** Parse a VALIDATED (<=2dp, accepted-format, non-negative) string to its
 * exact hundredths as a BigInt. Throws if the value is not acceptable -- call
 * sites validate first and surface a field error. */
export function toHundredths(raw: string): bigint {
  const s = (raw ?? "").trim();
  if (!isAcceptedNumberFormat(s)) {
    throw new Error(`not an accepted 2dp number: ${JSON.stringify(raw)}`);
  }
  const dot = s.indexOf(".");
  const intPart = dot < 0 ? s : s.slice(0, dot);
  let frac = dot < 0 ? "" : s.slice(dot + 1);
  // strip insignificant trailing zeros, then take (and pad) the first 2 digits
  frac = frac.replace(/0+$/, "");
  if (frac.length > 2) {
    throw new Error(`more than 2 decimal places: ${JSON.stringify(raw)}`);
  }
  frac = (frac + "00").slice(0, 2);
  return BigInt(intPart || "0") * 100n + BigInt(frac);
}

// --------------------------------------------------------------------------
// Exact scaled-integer canonical forward calculation
// --------------------------------------------------------------------------

/** ROUND_HALF_UP integer division for NON-NEGATIVE n, d (d > 0). */
function halfUp(n: bigint, d: bigint): bigint {
  return (2n * n + d) / (2n * d);
}

/** net unit cost (cents) = q2(unit * (1 - discount/100)). */
export function canonicalNetUnitCents(unitCents: bigint, discHundredthsPct: bigint): bigint {
  return halfUp(unitCents * (10000n - discHundredthsPct), 10000n);
}

/** line total (cents) = q2(qty * netUnit). */
export function canonicalLineTotalCents(qtyHundredths: bigint, netUnitCents: bigint): bigint {
  return halfUp(qtyHundredths * netUnitCents, 100n);
}

/** Format a cents BigInt as a fixed 2dp string ("2901" -> "29.01"). */
export function centsToText(cents: bigint): string {
  const whole = cents / 100n;
  const frac = cents % 100n;
  return `${whole.toString()}.${frac.toString().padStart(2, "0")}`;
}

export type LineCalcResult =
  | {
      ok: true;
      qtyHundredths: bigint;
      unitCents: bigint;
      discHundredthsPct: bigint;
      netUnitCents: bigint;
      lineTotalCents: bigint;
      totalText: string;
    }
  | { ok: false; field: "qty" | "cost_price" | "discount"; reason: string };

/** Validate the three raw inputs and, if all acceptable, compute the exact
 * canonical net unit and line total. Bounds (qty>0, cost>=0, 0<=disc<=100)
 * are enforced too so the same helper backs the field-level errors. */
export function canonicalLineTotal(
  qtyRaw: string,
  costRaw: string,
  discRaw: string,
): LineCalcResult {
  const disc = (discRaw ?? "").trim() === "" ? "0" : discRaw;
  if (exceeds2dp(qtyRaw)) return { ok: false, field: "qty", reason: "max 2 decimal places" };
  if (exceeds2dp(costRaw)) return { ok: false, field: "cost_price", reason: "max 2 decimal places" };
  if (exceeds2dp(disc)) return { ok: false, field: "discount", reason: "max 2 decimal places" };

  const qtyHundredths = toHundredths(qtyRaw);
  const unitCents = toHundredths(costRaw);
  const discHundredthsPct = toHundredths(disc);

  if (qtyHundredths <= 0n) return { ok: false, field: "qty", reason: "must be greater than 0" };
  if (discHundredthsPct > 10000n) return { ok: false, field: "discount", reason: "must be 0..100" };

  const netUnitCents = canonicalNetUnitCents(unitCents, discHundredthsPct);
  const lineTotalCents = canonicalLineTotalCents(qtyHundredths, netUnitCents);
  return {
    ok: true,
    qtyHundredths,
    unitCents,
    discHundredthsPct,
    netUnitCents,
    lineTotalCents,
    totalText: centsToText(lineTotalCents),
  };
}

// --------------------------------------------------------------------------
// Exact inverse: solve a 2dp cost from an entered line total
// --------------------------------------------------------------------------

export type InverseFailureReason =
  | "total_invalid"
  | "qty_invalid"
  | "qty_zero"
  | "discount_invalid"
  | "discount_out_of_range";

export type InverseResult =
  | {
      ok: true;
      action: "derive_cost";
      costCents: bigint;
      costText: string;
      canonicalTotalCents: bigint;
      canonicalTotalText: string;
      exact: boolean;
    }
  | {
      // discount == 100%: only a zero total is representable. The caller must
      // KEEP its current cost (never synthesize a zero cost).
      ok: true;
      action: "preserve_cost";
      canonicalTotalCents: bigint; // always 0
      canonicalTotalText: string; // "0.00"
      exact: boolean; // true iff the entered total was 0
    }
  | { ok: false; reason: InverseFailureReason };

/**
 * Given a user-entered line total, derive the canonical 2dp unit cost that
 * reproduces it under the net-unit-first calculation, with the discount held
 * fixed (discount is never reverse-derived). Always forward-recomputes the
 * canonical total and reports whether the entered total is exactly
 * representable, so the caller never persists an amount the backend would not.
 *
 * TOTAL over all three inputs: this is called on LIVE form state, so it fully
 * validates total, qty, AND discount (format, <=2dp, and numeric bounds)
 * before any `toHundredths` parse. No user-entered string can make it throw.
 * `discount == 100%` -> "preserve_cost" (division by the remaining percentage
 * is impossible; only a zero total is representable).
 */
export function deriveCostFromTotal(
  totalRaw: string,
  qtyRaw: string,
  discRaw: string,
): InverseResult {
  const disc = (discRaw ?? "").trim() === "" ? "0" : discRaw;

  // Format + precision guards first (exceeds2dp also rejects blank, lone ".",
  // malformed, sci-notation, sign, and >2dp), so toHundredths never throws.
  if (exceeds2dp(totalRaw)) return { ok: false, reason: "total_invalid" };
  if (exceeds2dp(qtyRaw)) return { ok: false, reason: "qty_invalid" };
  if (exceeds2dp(disc)) return { ok: false, reason: "discount_invalid" };

  const totalCents = toHundredths(totalRaw);
  const qtyHundredths = toHundredths(qtyRaw);
  const discHundredthsPct = toHundredths(disc);

  if (qtyHundredths <= 0n) return { ok: false, reason: "qty_zero" };
  if (discHundredthsPct > 10000n) return { ok: false, reason: "discount_out_of_range" };
  // total is guaranteed >= 0 (grammar rejects a sign), so no total-bound case.

  if (discHundredthsPct === 10000n) {
    // 100% discount: net unit is 0, so only a zero total is representable.
    return {
      ok: true,
      action: "preserve_cost",
      canonicalTotalCents: 0n,
      canonicalTotalText: "0.00",
      exact: totalCents === 0n,
    };
  }

  // netTarget (cents) ~ total/qty ; cost (cents) ~ net / (1 - disc/100)
  const netCents = halfUp(totalCents * 100n, qtyHundredths);
  const costCents = halfUp(netCents * 10000n, 10000n - discHundredthsPct);

  // forward re-verify through the canonical helper
  const netCheck = canonicalNetUnitCents(costCents, discHundredthsPct);
  const totalCheck = canonicalLineTotalCents(qtyHundredths, netCheck);

  return {
    ok: true,
    action: "derive_cost",
    costCents,
    costText: centsToText(costCents),
    canonicalTotalCents: totalCheck,
    canonicalTotalText: centsToText(totalCheck),
    exact: totalCheck === totalCents,
  };
}
